// Per-block floating chrome (drag handle + move up/down + delete).
//
// Custom plugin (no tippy) so we control hover behavior exactly.
// The handle sits in the editor's parent, absolute-positioned, and is treated
// as part of the hover zone alongside view.dom.

import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"

import {
  moveBlockUp,
  moveBlockDown,
  deleteBlockAt,
  findTopLevelBlockAtCoords,
  findTopLevelBlockById,
  moveBlockToTarget,
  isNoOpBlockMove,
} from "./commands"

export const BlockChrome = Extension.create({
  name: "blockChrome",

  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: new PluginKey("blockChrome"),
        view: (view) => new BlockChromeView(view, editor),
      }),
    ]
  },
})

class BlockChromeView {
  constructor(view, editor) {
    this.view = view
    this.editor = editor
    this.target = null

    // Host needs enough horizontal slack for the handle to sit OUTSIDE the
    // white card. Prefer the backdrop (surroundings target), fall back to
    // the controller's root, then to view.dom's immediate parent.
    this.host =
      view.dom.closest('[data-newsletter-editor-target="surroundings"]') ||
      view.dom.closest('[data-controller~="newsletter-editor"]') ||
      view.dom.parentElement ||
      document.body
    if (getComputedStyle(this.host).position === "static") {
      this.host.style.position = "relative"
    }

    this.handle = buildChrome((action) => this.#runAction(action))
    this.host.appendChild(this.handle)

    // Drop indicator — UI-only, never enters the document.
    this.indicator = document.createElement("div")
    this.indicator.className = "nl-block-drop-indicator"
    this.indicator.style.position = "absolute"
    this.indicator.style.display = "none"
    this.indicator.style.zIndex = "20"
    this.indicator.style.pointerEvents = "none"
    this.host.appendChild(this.indicator)

    this.handle.addEventListener("dragstart", (event) => this.#onDragStart(event))

    // Drag transport state. All structural work happens in #onDrop via a
    // single ProseMirror transaction.
    this.dragState = null
    this.currentDropTarget = null
    this.dragPointerY = 0
    this.isDragging = false

    this.boundOnDragOver = (event) => this.#onDragOver(event)
    this.boundOnDrop = (event) => this.#onDrop(event)
    this.boundOnDragEnd = () => this.#cleanupDrag()
    this.boundTrackPointer = (event) => {
      this.dragPointerY = event.clientY
    }

    this.onScroll = () => this.target && this.#position(this.target.pos)
    window.addEventListener("scroll", this.onScroll, true)
    window.addEventListener("resize", this.onScroll)

    // Initial placement based on the current selection.
    this.#positionAtSelection()
  }

  update(view, prevState) {
    const docChanged = view.state.doc !== prevState.doc
    const selectionChanged = !view.state.selection.eq(prevState.selection)
    if (docChanged || selectionChanged) {
      requestAnimationFrame(() => this.#positionAtSelection())
    }
  }

  destroy() {
    window.removeEventListener("scroll", this.onScroll, true)
    window.removeEventListener("resize", this.onScroll)
    this.#cleanupDrag()
    this.handle.remove()
    this.indicator?.remove()
  }

  // ----------------------------------------------------------------------

  // Show the handle next to whatever top-level block contains the current
  // selection. Hide it if no block-scoped selection is active.
  #positionAtSelection() {
    const sel = this.view.state.selection
    const $from = sel.$from
    let pos = null
    let node = null

    if ($from.depth >= 1) {
      pos = $from.before(1)
      node = this.view.state.doc.nodeAt(pos)
    } else if (sel.node) {
      // NodeSelection at doc level (e.g. clicking an atom directly).
      pos = sel.from
      node = sel.node
    }

    if (!node) {
      this.#hide()
      return
    }
    this.target = { node, pos }
    this.#position(pos)
  }

  #position(pos) {
    const dom = this.view.nodeDOM(pos)
    if (!(dom instanceof HTMLElement)) return
    const blockRect = dom.getBoundingClientRect()
    const hostRect = this.host.getBoundingClientRect()

    this.handle.style.display = "flex"
    // 5px up from the block top, 30px left of the block left edge.
    // Don't clamp — host (the bg backdrop) has plenty of slack on the left.
    this.handle.style.top = `${blockRect.top - hostRect.top - 9}px`
    this.handle.style.left = `${blockRect.left - hostRect.left - 48}px`
  }

  #hide() {
    this.handle.style.display = "none"
    this.target = null
  }

  #runAction(action) {
    if (!this.target) return
    const { pos } = this.target
    const view = this.view
    const props = {
      state: view.state,
      dispatch: view.dispatch.bind(view),
      tr: view.state.tr,
      view,
      editor: this.editor,
    }
    if (action === "up") moveBlockUp(pos)(props)
    else if (action === "down") moveBlockDown(pos)(props)
    else if (action === "delete") deleteBlockAt(pos)(props)
    // The doc just changed — reposition handle on the next frame.
    requestAnimationFrame(() => this.target && this.#position(this.target.pos))
  }

  // ----------------------------------------------------------------------
  // Drag/drop
  // ----------------------------------------------------------------------

  #onDragStart(event) {
    if (!this.target) {
      event.preventDefault()
      return
    }
    const { pos, node } = this.target
    // Re-resolve to make sure the targeted node still lives at pos.
    const liveNode = this.view.state.doc.nodeAt(pos)
    if (!liveNode || liveNode !== node) {
      event.preventDefault()
      return
    }

    this.dragState = {
      sourcePos: pos,
      sourceId: liveNode.attrs?.id || null,
      sourceNodeType: liveNode.type.name,
    }
    this.currentDropTarget = null

    // We manage the entire drop pipeline ourselves: no view.dragging slice,
    // no PM-driven drop. Browsers still require dataTransfer to be touched
    // for the drag to start at all.
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("application/x-newsletter-block", liveNode.attrs?.id || "1")
    event.dataTransfer.setData("text/plain", " ")

    const dom = this.view.nodeDOM(pos)
    if (dom instanceof HTMLElement) {
      event.dataTransfer.setDragImage(dom, 10, 10)
    }

    // Capture-phase listeners on view.dom so we run before ProseMirror's
    // built-in drop handlers can interfere.
    this.view.dom.addEventListener("dragover", this.boundOnDragOver, { capture: true })
    this.view.dom.addEventListener("drop", this.boundOnDrop, { capture: true })
    document.addEventListener("dragend", this.boundOnDragEnd)
    document.addEventListener("dragover", this.boundTrackPointer, { passive: true })

    this.#startAutoscroll()
  }

  #onDragOver(event) {
    if (!this.dragState) return

    // Only allow drops inside the editor.
    if (!this.view.dom.contains(event.target) && event.target !== this.view.dom) {
      this.#hideIndicator()
      this.currentDropTarget = null
      return
    }

    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = "move"

    const candidate = findTopLevelBlockAtCoords(this.view, {
      left: event.clientX,
      top: event.clientY,
    })
    if (!candidate) {
      this.#hideIndicator()
      this.currentDropTarget = null
      return
    }

    const midY = candidate.rect.top + candidate.rect.height / 2
    const placement = event.clientY < midY ? "before" : "after"

    const sourcePos = this.#resolveSourcePos()
    if (sourcePos === null) {
      this.#hideIndicator()
      this.currentDropTarget = null
      return
    }

    if (isNoOpBlockMove(sourcePos, candidate.pos, placement, this.view.state.doc)) {
      this.#hideIndicator()
      this.currentDropTarget = null
      return
    }

    this.currentDropTarget = { pos: candidate.pos, placement, rect: candidate.rect }
    this.#showIndicator(candidate.rect, placement)
  }

  #onDrop(event) {
    if (!this.dragState || !this.currentDropTarget) {
      this.#cleanupDrag()
      return
    }
    event.preventDefault()
    event.stopPropagation()
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation()
    }

    const sourcePos = this.#resolveSourcePos()
    if (sourcePos === null) {
      this.#cleanupDrag()
      return
    }
    const { pos: targetPos, placement } = this.currentDropTarget

    const view = this.view
    const props = {
      state: view.state,
      dispatch: view.dispatch.bind(view),
      tr: view.state.tr,
      view,
      editor: this.editor,
    }
    moveBlockToTarget(sourcePos, targetPos, placement)(props)

    this.#cleanupDrag()
    // The doc just changed — reposition handle on the next frame.
    requestAnimationFrame(() => this.#positionAtSelection())
  }

  // Re-resolves the dragged source position from its stable id when
  // possible. Defends against a stale sourcePos after any concurrent
  // transaction (autosave, remote, etc.) lands mid-drag.
  #resolveSourcePos() {
    if (!this.dragState) return null
    if (this.dragState.sourceId) {
      const fresh = findTopLevelBlockById(this.view.state.doc, this.dragState.sourceId)
      if (fresh) {
        this.dragState.sourcePos = fresh.pos
        return fresh.pos
      }
    }
    const node = this.view.state.doc.nodeAt(this.dragState.sourcePos)
    if (!node || node.type.name !== this.dragState.sourceNodeType) return null
    return this.dragState.sourcePos
  }

  #showIndicator(rect, placement) {
    const hostRect = this.host.getBoundingClientRect()
    const y = placement === "before" ? rect.top : rect.bottom
    this.indicator.style.display = "block"
    this.indicator.style.top = `${y - hostRect.top - 1}px`
    this.indicator.style.left = `${rect.left - hostRect.left}px`
    this.indicator.style.width = `${rect.width}px`
  }

  #hideIndicator() {
    if (this.indicator) this.indicator.style.display = "none"
  }

  #cleanupDrag() {
    this.dragState = null
    this.currentDropTarget = null
    this.#hideIndicator()
    this.#stopAutoscroll()
    this.view.dom.removeEventListener("dragover", this.boundOnDragOver, { capture: true })
    this.view.dom.removeEventListener("drop", this.boundOnDrop, { capture: true })
    document.removeEventListener("dragend", this.boundOnDragEnd)
    document.removeEventListener("dragover", this.boundTrackPointer, { passive: true })
  }

  // Browsers stopped doing native scroll-while-dragging in most cases. Nudge
  // window.scrollY each frame while the cursor is near the viewport edges.
  #startAutoscroll() {
    if (this.isDragging) return
    this.isDragging = true
    this.dragPointerY = 0

    const EDGE = 80
    const MAX_SPEED = 18
    const step = () => {
      if (!this.isDragging) return
      const y = this.dragPointerY
      const distTop = y
      const distBottom = window.innerHeight - y
      let delta = 0
      if (y > 0 && distTop < EDGE) {
        delta = -Math.ceil(((EDGE - distTop) / EDGE) * MAX_SPEED)
      } else if (distBottom < EDGE) {
        delta = Math.ceil(((EDGE - distBottom) / EDGE) * MAX_SPEED)
      }
      if (delta !== 0) window.scrollBy(0, delta)
      this.scrollRaf = requestAnimationFrame(step)
    }
    this.scrollRaf = requestAnimationFrame(step)
  }

  #stopAutoscroll() {
    this.isDragging = false
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf)
      this.scrollRaf = null
    }
  }
}

function buildChrome(onAction) {
  const root = document.createElement("div")
  root.className = "nl-block-chrome"
  root.contentEditable = "false"
  root.style.position = "absolute"
  root.style.zIndex = "15"
  root.style.display = "none"
  root.style.pointerEvents = "auto"
  root.innerHTML = `
    <button type="button" draggable="true"
            class="nl-block-chrome__btn nl-block-chrome__btn--drag"
            data-action="drag" title="Drag to reorder" aria-label="Drag to reorder">
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
        <circle cx="5" cy="3" r="1.4"/><circle cx="11" cy="3" r="1.4"/>
        <circle cx="5" cy="8" r="1.4"/><circle cx="11" cy="8" r="1.4"/>
        <circle cx="5" cy="13" r="1.4"/><circle cx="11" cy="13" r="1.4"/>
      </svg>
    </button>
    <button type="button" class="nl-block-chrome__btn" data-action="up" title="Move up" aria-label="Move up">
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="4 10 8 6 12 10"/>
      </svg>
    </button>
    <button type="button" class="nl-block-chrome__btn" data-action="down" title="Move down" aria-label="Move down">
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="4 6 8 10 12 6"/>
      </svg>
    </button>
    <button type="button" class="nl-block-chrome__btn nl-block-chrome__btn--danger" data-action="delete" title="Delete block" aria-label="Delete block">
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/>
      </svg>
    </button>
  `
  root.addEventListener("mousedown", (event) => {
    const btn = event.target.closest("[data-action]")
    if (btn && btn.dataset.action !== "drag") event.preventDefault()
  })
  root.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-action]")
    if (!btn) return
    const action = btn.dataset.action
    if (action === "drag") return
    event.preventDefault()
    onAction(action)
  })
  return root
}
